import { parseUrl } from 'query-string';
import { END_ADUNIT, MAX_BANNER_HEIGHT, MIN_BANNER_HEIGHT } from '../../constants';

const getParsedSizes = (sizes = '') => {
  if (typeof sizes !== 'string') return [];

  const sanitizedSizes = sizes.replace(/\s+/, '').toLowerCase();
  const sizeGroups = sanitizedSizes.split(',');

  return sizeGroups.map(group => group.split('x').map(Number));
};

const addPosnToTargetings = (targetings, index) => {
  const posn = `posn=${index}`;
  return targetings ? `${targetings},${posn}` : posn;
};

const buildTargetingValue = valueTargeting =>
  valueTargeting.includes('[') && valueTargeting.includes(']')
    ? valueTargeting.replace(/\;|\[|\]/gm, char => (char === ';' ? ',' : '')).split(',')
    : valueTargeting;

const setCustomTargetings = (targetings, renderCounter) => {
  let bannerTargetings = {};

  const updatedTargetings = renderCounter
    ? addPosnToTargetings(targetings, renderCounter)
    : targetings;

  const isValidTargeting = updatedTargetings && typeof updatedTargetings === 'string';

  if (isValidTargeting) {
    const customTargeting = {};

    let splitTargeting = updatedTargetings.split(',');
    splitTargeting = { ...splitTargeting };

    Object.keys(splitTargeting).forEach(key => {
      const [keyTargeting, valueTargeting] = splitTargeting[key].split('=');

      if (keyTargeting && valueTargeting) {
        customTargeting[keyTargeting] = buildTargetingValue(valueTargeting);
      }
    });
    bannerTargetings = { ...bannerTargetings, ...customTargeting };
  }

  return bannerTargetings;
};

const buildParsedAdunit = (path, countContentHierarchies, baseAdunit, adunit) => {
  if (!Number.isInteger(countContentHierarchies)) return '';

  const base = baseAdunit || process.env.DFP_BASE_ADUNIT;
  const hasChildren = countContentHierarchies !== 0;
  const { url } = parseUrl(path);
  let urlParts = url
    .split('/')
    .filter(Boolean)
    .map(urlPart => urlPart.replace(/-/g, ''));
  urlParts = urlParts.slice(0, hasChildren ? urlParts.length : urlParts.length - 1).join('/');
  urlParts = urlParts ? `${urlParts}/` : urlParts;
  const endAdunit = adunit || END_ADUNIT;

  return `${base}/${urlParts}${endAdunit}`;
};

const getBannerSizes = (sizeId, bannerSizes) =>
  sizeId && bannerSizes && bannerSizes.length
    ? bannerSizes.map(({ width, height }) => [width, height])
    : null;

const buildBannerSizes = (sizeId, bannerSizes, sizes) => {
  let parsedSizes = getBannerSizes(sizeId, bannerSizes);

  if (!parsedSizes && sizes) parsedSizes = getParsedSizes(sizes);

  return parsedSizes;
};

const buildContextualTargeting = (actionKey, props = {}, propsToDisplay = []) => {
  if (actionKey && props[actionKey] && props[actionKey][0]) {
    const data = props[actionKey][0];
    return buildPropsData(data, propsToDisplay);
  }
  return {};
};

const buildSizeMapping = (sizeId, bannerSizes) => {
  let sizeMapping = [];

  if (sizeId && bannerSizes && bannerSizes.length) {
    const groupedViewports = bannerSizes.reduce((acc, { width, height, viewports = [] }) => {
      const size = width && height ? [width, height] : null; // if one dimension is falsey then it shouldn't display

      if (viewports) {
        viewports.forEach(({ width: viewportWidth, height: viewportHeight }) => {
          const key = `${viewportWidth || 0}x${viewportHeight || 0}`;
          acc[key] = acc[key] || [];
          if (size) acc[key].push(size);
        });
      }
      return acc;
    }, {});

    sizeMapping = Object.keys(groupedViewports).map(keyGroupedViewport => {
      const [viewportWidth, viewportHeight] = keyGroupedViewport.split('x');
      return {
        viewport: [Number(viewportWidth), Number(viewportHeight)],
        sizes: groupedViewports[keyGroupedViewport]
      };
    });
  }

  return sizeMapping;
};

const getMaxBannerHeight = bannerSizes => {
  if (Array.isArray(bannerSizes) && bannerSizes.length > 0) {
    return Math.max(...bannerSizes.map(o => o.height));
  }
  return MAX_BANNER_HEIGHT;
};

const getMinBannerHeight = bannerSizes => {
  if (Array.isArray(bannerSizes) && bannerSizes.length > 0) {
    return Math.min(...bannerSizes.map(o => o.height));
  }
  return MIN_BANNER_HEIGHT;
};

const buildUserQuery = userProps => {
  if (!userProps || !userProps.length) return '';
  const basicProps = [];
  const nestedProps = {};
  userProps.forEach(({ propertiesToDisplay }) => {
    const [propKey] = propertiesToDisplay;
    if (!propKey || propKey === 'id') return;
    if (propKey.indexOf('.') !== -1) {
      const [baseProp, nestedProp] = propKey.split('.');
      if (nestedProps[baseProp]) nestedProps[baseProp].push(nestedProp);
      else nestedProps[baseProp] = [nestedProp];
    } else basicProps.push(propKey);
  });

  Object.keys(nestedProps).forEach(nestedKey => {
    const nested = nestedProps[nestedKey];
    basicProps.push(nestedKey);
    basicProps.push('{');
    nested.forEach(prop => basicProps.push(prop));
    basicProps.push('}');
  });

  return basicProps.join(' ');
};

const buildPropsData = (data, props) => {
  if (!data || !props.length) return {};
  const propsWithData = {};

  props.forEach(({ propertiesToDisplay, label }) => {
    const [key] = propertiesToDisplay;
    if (!key) return;

    const parsedKey = key.split('.');
    const value = getNestedValue(parsedKey, data);
    const labelToUse = label || key;

    propsWithData[labelToUse] = value;
  });
  return propsWithData;
};

const parseArrayValues = (arr, key) => arr.map(obj => obj[key]).filter(Boolean);

const getNestedValue = (keys, object) => {
  const key = keys.shift();
  const nestedValue = Array.isArray(object) ? parseArrayValues(object, key) : object[key];
  if (!nestedValue) return null;
  return keys.length ? getNestedValue(keys, nestedValue) : nestedValue;
};

export {
  getParsedSizes,
  setCustomTargetings,
  buildParsedAdunit,
  buildBannerSizes,
  buildContextualTargeting,
  buildSizeMapping,
  getMaxBannerHeight,
  getMinBannerHeight,
  buildPropsData,
  buildUserQuery
};
